// ChatInput — input box + lens toggle + model picker + submit. Two layouts:
//  - hero (centered on empty chat)
//  - dock (fixed bottom on chat-with-answer)

import { useEffect, useRef, useState } from 'react';
import type { ModelChoice } from '../types';

interface Props {
  variant: 'hero' | 'dock';
  lens: 'objective' | 'lens';
  onLensChange: (lens: 'objective' | 'lens') => void;
  model: ModelChoice;
  onModelChange: (m: ModelChoice) => void;
  disabled?: boolean;
  placeholder?: string;
  onSubmit: (prompt: string) => void;
  brainCount?: number;
  /** Optional focus callbacks for parent components that want to respond to
   * the textarea gaining/losing focus (e.g., hero state shifting the layout). */
  onFocus?: () => void;
  onBlur?: (currentValue: string) => void;
}

const MODEL_OPTIONS: { id: ModelChoice; label: string; sub: string }[] = [
  { id: 'auto', label: 'Auto', sub: 'We pick the right model for each question.' },
  { id: 'claude-haiku-4-5', label: 'Haiku 4.5', sub: 'Fast and light — good for quick, focused questions.' },
  { id: 'claude-sonnet-4-6', label: 'Sonnet 4.6', sub: 'Balanced — a solid default for most technical work.' },
  { id: 'claude-opus-4-7', label: 'Opus 4.7', sub: 'Most capable — best for the hardest reasoning.' },
];

export function ChatInput({
  variant, lens, onLensChange, model, onModelChange, disabled, placeholder, onSubmit, brainCount,
  onFocus, onBlur,
}: Props) {
  const [value, setValue] = useState('');
  const ref = useRef<HTMLTextAreaElement>(null);
  const [modelMenuOpen, setModelMenuOpen] = useState(false);
  const modelMenuRef = useRef<HTMLDivElement>(null);

  // Auto-grow textarea
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = '28px';
    el.style.height = Math.min(el.scrollHeight, 240) + 'px';
  }, [value]);

  // Close model menu on outside click
  useEffect(() => {
    if (!modelMenuOpen) return;
    function onDoc(e: MouseEvent) {
      if (modelMenuRef.current && !modelMenuRef.current.contains(e.target as Node)) {
        setModelMenuOpen(false);
      }
    }
    setTimeout(() => document.addEventListener('mousedown', onDoc), 0);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [modelMenuOpen]);

  function handleSubmit() {
    const v = value.trim();
    if (!v || disabled) return;
    onSubmit(v);
    setValue('');
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }

  const wrapperClass =
    'bg-card border border-border/70 rounded-xl shadow-sm transition-all duration-200 focus-within:border-ink/40 focus-within:shadow-md overflow-hidden';

  const modelLabel = MODEL_OPTIONS.find((m) => m.id === model)?.label ?? 'Auto';

  return (
    <div className={`${wrapperClass} ${variant === 'dock' ? 'max-w-[1100px] mx-auto' : ''}`}>
      <div className="flex items-end px-5 pt-4 pb-1 gap-3">
        <textarea
          ref={ref}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKeyDown}
          onFocus={onFocus}
          onBlur={() => onBlur?.(value)}
          placeholder={placeholder ?? 'Ask a follow-up...'}
          rows={1}
          disabled={disabled}
          className="flex-1 border-none outline-none font-sans text-[15px] leading-relaxed text-ink resize-none bg-transparent placeholder:text-text3 disabled:opacity-50 caret-accent"
          style={{ height: 28 }}
        />
      </div>

      <div className="flex flex-wrap justify-between items-center px-3 py-2 gap-2 border-t border-hairline/60 bg-card">
        <div className="flex items-center gap-1.5">
          {/* Lens toggle */}
          <div className="inline-flex bg-bg/60 rounded-full p-0.5 text-xs">
            <LensButton active={lens === 'objective'} onClick={() => onLensChange('objective')}>Objective</LensButton>
            <LensButton active={lens === 'lens'} onClick={() => onLensChange('lens')}>Through Element's lens</LensButton>
          </div>

          {/* Model picker */}
          <div className="relative" ref={modelMenuRef}>
            <button
              onClick={() => setModelMenuOpen(!modelMenuOpen)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-mono transition-colors ${
                model === 'auto'
                  ? 'bg-bg/60 text-text2 hover:text-ink'
                  : 'bg-ink/[0.08] text-ink hover:bg-ink/[0.12]'
              }`}
              title="Pick model"
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="12" cy="12" r="3" />
                <path d="M12 1v6M12 17v6M4.22 4.22l4.24 4.24M15.54 15.54l4.24 4.24M1 12h6M17 12h6M4.22 19.78l4.24-4.24M15.54 8.46l4.24-4.24" />
              </svg>
              {modelLabel}
            </button>

            {modelMenuOpen && (
              <div className="absolute bottom-9 left-0 w-72 bg-card border border-border rounded-lg shadow-md py-1 z-30">
                <div className="font-display text-[10px] uppercase tracking-wide12 text-text3 px-3 pt-2 pb-1">
                  Model
                </div>
                {MODEL_OPTIONS.map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => {
                      onModelChange(opt.id);
                      setModelMenuOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 transition-colors ${
                      model === opt.id ? 'bg-ink/[0.06]' : 'hover:bg-ink/[0.04]'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`text-[13px] ${model === opt.id ? 'font-semibold text-ink' : 'text-ink'}`}>
                        {opt.label}
                      </span>
                      {model === opt.id && (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="text-accent">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </div>
                    <div className="text-[11px] text-text2 mt-0.5">{opt.sub}</div>
                  </button>
                ))}
                <div className="px-3 py-2 border-t border-hairline mt-1">
                  <p className="text-[10px] text-text3 leading-relaxed">
                    Auto chooses for you. Override anytime — your pick applies to the next message only.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-[10px] text-text3 hidden sm:inline">
            {brainCount !== undefined && brainCount > 0
              ? `${brainCount} brain${brainCount === 1 ? '' : 's'} loaded`
              : 'Ready when you are'}
          </span>
          <button
            onClick={handleSubmit}
            disabled={disabled || value.trim().length === 0}
            aria-label="Submit"
            className="bg-ink text-bg w-9 h-9 rounded-lg cursor-pointer grid place-items-center transition-all hover:bg-accent disabled:opacity-40 disabled:hover:bg-ink"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

function LensButton({ active, children, onClick }: { active: boolean; children: React.ReactNode; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1 rounded-full text-[11px] font-medium transition-colors ${
        active ? 'bg-ink text-bg' : 'text-text2 hover:text-ink'
      }`}
    >
      {children}
    </button>
  );
}
